import { Link, useLocation, useNavigate } from "react-router-dom";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { useToast } from "@/components/ui/UseToast";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "@/app/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoginFormSchema } from "@/schema/LoginSchema";
import { useForm } from "react-hook-form";
import { customerLogin, createSelectedUser } from "@/features/auth/AuthSlice";
import { IMAGES } from "@/assets/IMAGES";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import GoogleLogin from "./google-login/GoogleAuth";
import * as z from "zod";

const initialValue = {
  identifier: "",
  rememberme: false,
};

const { VITE_APP_SITEKEY, VITE_ORG_ID, 
  VITE_IS_PRODUCTION, VITE_IS_LOCAL, VITE_LOCAL_URL } =
  import.meta.env;

type LoginFormValues = {
  identifier: string;
  rememberme: boolean;
};

export interface ItsData {
  its_id?: string;
  first_name: string;
  email: string;
  last_name: string;
  username?:string;
}

export default function UserLogin() {
  useDocumentTitle("User Login");
  const { toast } = useToast();
  const navigate = useNavigate(); // Initialize the navigation hook
  const dispatch = useDispatch<AppDispatch>();
  const userPersona = localStorage.getItem("userType");

  const { loading, error, isEmailVerify } = useSelector(
    (state: RootState) => state.auth
  );

  const [users, setUsers] = useState<ItsData[]>([]);
  const [loadingUser, setLoadingUser] = useState<string | null>(null);

  const isAuthenticated = Boolean(localStorage.getItem("userToken"));
  const location = useLocation();

  const handleClick = () => {
    navigate("/"); // Navigate to the root path when clicked
  };
  useEffect(() => {
    if (
      isAuthenticated &&
      userPersona === "RemoteUser" &&
      location.pathname === "/user-login"
    ) {
      navigate("/user/home");
    }
  }, [location]);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: async (data) => {
      const errors: Record<string, any> = {};
      if (!data.identifier) {
        errors.identifier = {
          type: "required",
          message: "Email/Its is required.",
        };
      } else {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
        const idRegex = /^\d{8}$/;
        if (
          !emailRegex.test(data.identifier) &&
          !idRegex.test(data.identifier)
        ) {
          if (data.identifier.includes("@")) {
            errors.identifier = {
              type: "validate",
              message: "Invalid email format.",
            };
          } else {
             errors.identifier = {
               type: "validate",
               message: "Invalid ITS ID format.",
             };
          }
        }
      }
      return { values: data, errors };
    },
    defaultValues: initialValue,
  });

  const onSubmit = async (data: LoginFormValues) => {
    const identifier = data.identifier.toLowerCase(); // Convert identifier to lowercase if it's an email
    // Dispatch the login thunk
    dispatch(
      customerLogin({
        identifier,
        org_id: VITE_ORG_ID,
        rememberme: false,
        domain:
          VITE_IS_LOCAL === "true"
            ? VITE_LOCAL_URL
            : VITE_IS_PRODUCTION === "true"
              ? window.location.hostname
              : window.location.host,
      })
    )
      .unwrap()
      .then((response) => {
        if (response.users?.length > 1) {
          setUsers(response.users);
        } else {
          toast({
            variant: "success",
            description: (
              <div className="flex items-center space-x-2 w-full h-10">
                <img
                  src={IMAGES.Status_icon}
                  alt="Success"
                  className="w-6 h-6"
                />
                <span>{response.message}</span>
              </div>
            ),
          });
          navigate("/user-login-otp", {
            state: { identifier, emailResponse: response },
          });
        }
      })
      .catch((error) => {
        console.error("Error Message", error === "User not Regisered.");
        if (error === "User not Regisered.") {
          toast({
            variant: "destructive",
            description: "User not Registered.",
          });
          navigate("/user-register", {
            state: { identifier, message: "User not Registered." },
          });
        } else {
          toast({
            variant: "destructive",
            description: error || "An error occurred.",
          });
        }
      });
  };

  const handleUserSelection = (its_id: string) => {
    if (loadingUser) return; // Prevent multiple clicks
    setLoadingUser(its_id); // Set loading state for clicked user

    const org_id = VITE_ORG_ID; // Get org_id from env

    dispatch(
      createSelectedUser({
        identifier: its_id,
        org_id,
        domain:
          VITE_IS_LOCAL === "true"
            ? VITE_LOCAL_URL
            : VITE_IS_PRODUCTION === "true"
              ? window.location.hostname
              : window.location.host,
      })
    )
      .unwrap()
      .then((response) => {
        toast({
          variant: "success",
          description: (
            <div className="flex items-center space-x-2 w-full h-10">
              <img src={IMAGES.Status_icon} alt="Success" className="w-6 h-6" />
              <span>{response.message}</span>
            </div>
          ),
        });
        navigate("/user-login-otp", {
          state: { identifier: its_id, emailResponse: response },
        });
      })
      .catch((error) => {
        toast({
          variant: "destructive",
          description: error || "An error occurred while selecting user.",
        });
        setLoadingUser(null); // Reset loading state on failure
      });
  };

  const Divider = ({ text }: { text: string }) => (
    <div className="flex gap-2.5 items-center mt-6 text-sm text-white">
      <div className="shrink-0 self-stretch my-auto h-px border border-solid bg-slate-200 border-slate-200 w-[104px]" />
      <div>{text}</div>
      <div className="shrink-0 self-stretch my-auto h-px border border-solid bg-slate-200 border-slate-200 w-[104px]" />
    </div>
  );
  return (
    <div className="loginpage-image">
      <div className="max-w-[1800px] mx-auto xs:mx-0">
        <div className="flex sm:mx-16 justify-center sm:justify-between items-center h-dvh xs:mx-0  ">
          <div className=" flex flex-col gap-2 xs:gap-0"></div>
          <div className="px-2  md:pt-0 md:pb-10">
            <Card className=" max-w-md bg-white/5 backdrop-blur-lg rounded-3xl border border-white/40 shadow-lg">
              <CardHeader>
                <CardTitle>
                  <div className="gap-1 flex justify-center items-center">
                    <div className="flex flex-col gap-2">
                      <h1 className="font-bold  tracking-wider leading-5 text-[1.3rem] text-primary">
                        Log In
                      </h1>
                      <p className="text-textwhite leading-5  font-semibold text-[1.8rem]">
                        Your Account
                      </p>
                      <p className="text-textgray leading-5 text-[0.9rem]">
                        Log in to access all the features and make the most of
                        your experience.
                      </p>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {users.length > 0 && (
                  <p className="text-textgray leading-5 text-[0.9rem]">
                    Select a user below to logIn
                  </p>
                )}
                {users.length > 0 ? (
                  <div className="max-h-60 overflow-y-auto fancy-scroll">
                    {users.map((user) => (
                      <div
                        key={user.its_id}
                        className={`p-2 bg-secondary rounded-lg my-2 cursor-pointer ${
                          loadingUser
                            ? "cursor-not-allowed opacity-50"
                            : "hover:bg-gray-200"
                        }`}
                        onClick={() => {
                          if (!loadingUser) {
                            const userId =
                              user?.its_id?.toString() || user?.username;
                            if (userId) {
                              handleUserSelection(userId);
                            }
                          }
                        }}
                      >
                        <p className="text-primary font-bold">{user.its_id}</p>
                        <div className="overflow-hidden whitespace-nowrap text-ellipsis">
                          <p className="text-primary font-bold">
                            {user.first_name} {user.last_name}
                          </p>
                        </div>
                        <p className="text-primary">{user.email}</p>
                        {loadingUser ===
                          (user?.its_id?.toString() || user?.username) && (
                          <span className="ml-2 text-sm text-black animate-pulse flex flex-row">
                            Logging in...
                            <div className="w-5 h-5 ml-2 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className="space-y-4 "
                    >
                      <div
                        className={`flex items-center bg-[#373737] 
                  bg-opacity-40 custom-box-shadow w-full gap-2 px-4 py-2 
                  rounded-md border border-checkboxborder focus-within:outline-none
                   focus-within:ring-2 focus-within:ring-white ${
                     errors.identifier ? "ring-2 ring-red-500" : ""
                   }`}
                      >
                        <input
                          maxLength={50}
                          id="identifier"
                          type="text"
                          placeholder="Enter your Email or 8-digit ITS ID"
                          className={`w-full bg-transparent border-checkboxborder text-textgray outline-none`}
                          {...register("identifier", {
                            required: "Email/ITS is required.",
                            validate: (value) => {
                              const emailRegex =
                                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
                              const idRegex = /^\d{8}$/;
                              if (
                                !emailRegex.test(value) &&
                                !idRegex.test(value)
                              ) {
                                if (value.includes("@")) {
                                  return "Invalid email format.";
                                } else {
                                  return "Invalid ITS ID format.";
                                }
                              }
                            },
                            maxLength: 50,
                          })}
                        />
                      </div>
                      {errors.identifier?.message && (
                        <span className="text-xs text-red-500 font-poppins ">
                          {errors.identifier.message as string}
                        </span>
                      )}
                      {errors.identifier?.type == "maxLength" && (
                        <span className="text-xs text-red-400 font-poppins ">
                          Max length exceeded
                        </span>
                      )}
                      <LoadingButton
                        loading={loading}
                        disabled={loading} // Disable button when loading
                        className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 bg-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-primary-400 dark:hover:bg-primary-500 text-black hover:text-white font-normal text-base"
                      >
                        {loading ? "Logging In" : "LogIn"}
                      </LoadingButton>
                    </form>
                    <div className="flex justify-center items-center">
                      <Divider text="Or" />
                    </div>
                    <div className="flex w-full justify-center items-center gap-2 pt-2">
                      <GoogleLogin setUsers={setUsers} />
                    </div>
                  </>
                )}
                {/* <div className="flex justify-center items-center mt-6">
                  <h3 className="text-white font-normal  text-sm mx-2 text-center">
                    By using this app you agree to the Privacy statements
                    <p className="Poppins text-textgray text-sm">
                      <Link
                        className="text-primary underline"
                        to="/privacy-policy"
                      >
                        Privacy Statement
                      </Link>{" "}
                      -{" "}
                      <Link
                        className="text-primary underline"
                        to="/terms-and-conditions"
                      >
                        Terms & condition{" "}
                      </Link>
                    </p>
                  </h3>
                </div> */}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
